import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Chip,
  IconButton,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { Comment, CommentOutlined, Delete, ExpandMore } from "@mui/icons-material";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { buildAllocationIntent, calculateItemAmounts, getPooledQuantity } from "./helper";
import RemarksDialog from "./remarks-dialog";

const PRItemRow = ({
  prItem,
  subcategoryList,
  allVendors,
  budgetHeadName,
  onUpdate,
  onDelete,
  headerRequiredByDate,
  prDate,
  // Resolved concrete stores this item targets -- empty for SELF/SINGLE_STORE (single-site,
  // no per-store split needed), populated for every multi-store HEAD_OFFICE destination.
  targetStores = [],
}) => {
  const [itemsForRow, setItemsForRow] = useState([]);
  const [itemVendors, setItemVendors] = useState(allVendors);
  const [perStoreAnchor, setPerStoreAnchor] = useState(null);
  const [remarksOpen, setRemarksOpen] = useState(false);
  const isMultiStore = targetStores.length > 1;
  const usesAllocations = targetStores.length > 0;
  const isPerStoreOpen = Boolean(perStoreAnchor);

  // Sync vendor list when parent prop changes (initial load delay)
  useEffect(() => {
    if (!prItem.item) setItemVendors(allVendors);
  }, [allVendors]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (field, value) => {
    const updated = { ...prItem, [field]: value };
    onUpdate({ ...updated, ...calculateItemAmounts(updated) });
  };

  // SELF items (targetStores is deliberately empty -- there's exactly one implicit site, the
  // requesting HO user's own, with no per-store list to show) still arrive with a populated,
  // single-entry `allocations` array from creation time (see createPR's SELF branch).
  // getPooledQuantity/buildEditPayload both treat a non-empty allocations array as
  // authoritative over this flat field, so it must be cleared here or an edited quantity is
  // silently ignored in favor of the stale allocation value -- mirrors single-store-row.js's
  // identical handling for the SINGLE_STORE PR type.
  const handleFlatQuantityChange = (value) => {
    const updated = { ...prItem, quantity: value, allocations: [] };
    onUpdate({ ...updated, ...calculateItemAmounts(updated) });
  };

  // Entered quantity is per store, replicated in full to every target store -- never
  // divided (2 stores + qty 2 means each store gets 2, pooled total 4). Any per-store
  // override made below via handleStoreAllocationChange is reset by this, since a change to
  // the flat field is a fresh "apply this to every store" instruction.
  const handlePooledQuantityChange = (value) => {
    const allocations = buildAllocationIntent(value, targetStores, prItem.allocations);
    const updated = { ...prItem, quantity: value, allocations };
    onUpdate({ ...updated, ...calculateItemAmounts(updated) });
  };

  // Overrides one store's quantity after the flat entry above seeded all of them equally.
  const handleStoreAllocationChange = (storeId, value) => {
    const qty = parseFloat(value) || 0;
    const baseAllocations =
      prItem.allocations && prItem.allocations.length > 0
        ? prItem.allocations
        : buildAllocationIntent(prItem.quantity, targetStores);
    const allocations = baseAllocations.map((a) =>
      String(a.storeId) === String(storeId) ? { ...a, requestedQuantity: qty } : a
    );
    const updated = { ...prItem, allocations };
    onUpdate({ ...updated, quantity: getPooledQuantity(updated), ...calculateItemAmounts(updated) });
  };

  const loadItemsBySubcategory = (subcategory) => {
    const { id = "" } = subcategory || {};
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_ITEM_LIST}`,
      null,
      { page: 1, subCategory: id, limit: 1000 }
    )
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.results || [];
        setItemsForRow(items);
      })
      .catch(console.log);
  };

  // Rows that arrive pre-filled (Excel upload, or an existing PR loaded for edit) already
  // know their subcategory and item, but itemsForRow otherwise only ever gets populated by
  // the user interacting with the Subcategory dropdown below (handleSubcategoryChange) —
  // without this, the Item Autocomplete would show blank even though prItem.item is
  // already correctly set. Only fires when there's a pre-set subcategory and nothing has
  // loaded yet, so a freshly added blank manual row is unaffected.
  useEffect(() => {
    if (prItem._subCategory && itemsForRow.length === 0) {
      loadItemsBySubcategory(prItem._subCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prItem._subCategory]);

  const handleSubcategoryChange = (val) => {
    const updated = {
      ...prItem,
      _subCategory: val,
      // Subcategory always resolves to exactly one parent category (enforced by the
      // Category/Subcategory master data), so its own populated `category` is always an
      // unambiguous category-mapping selector -- same principle buildPayload already relies
      // on for these two fields.
      category: val?.category?._id ?? val?.category?.id ?? val?.category ?? undefined,
      subCategory: val?._id ?? val?.id ?? undefined,
      // categoryMappingId can't be resolved until a specific Item is chosen (see
      // handleItemChange) -- cleared here since changing Subcategory always clears the Item.
      categoryMappingId: undefined,
      item: null,
      itemName: "",
      hsnCode: "",
      uom: "",
      taxRate: "",
      estimatedRate: "",
      description: "",
      rateContract: false,
      vendorCode: "",
      vendorName: "",
      vendorMappings: [],
      _item: null,
    };
    onUpdate({ ...updated, ...calculateItemAmounts(updated) });
    if (val) loadItemsBySubcategory(val);
    else setItemsForRow([]);
  };

  const handleItemChange = (val) => {
    if (!val) {
      const cleared = {
        ...prItem,
        item: null,
        itemName: "",
        hsnCode: "",
        uom: "",
        taxRate: "",
        estimatedRate: "",
        description: "",
        rateContract: false,
        vendorCode: "",
        vendorName: "",
        vendorMappings: [],
        categoryMappingId: undefined,
        _item: null,
      };
      setItemVendors(allVendors);
      onUpdate({ ...cleared, ...calculateItemAmounts(cleared) });
      return;
    }

    // The item's own categoryMappings pair matching this row's already-selected Subcategory
    // -- the row's `subCategory` (set by handleSubcategoryChange) is always unambiguous, so
    // this is a direct lookup, never a guess, even for an item with more than one pair.
    const matchingMapping = (val.categoryMappings || []).find(
      (m) => String(m.subCategory?._id ?? m.subCategory?.id ?? m.subCategory) === String(prItem.subCategory)
    );

    const baseUpdate = {
      ...prItem,
      item: val._id || val.id || null,
      itemName: val.name || "",
      hsnCode: val.hsnCode || "",
      uom: val.uom || "",
      taxRate: val.taxPercentage != null ? String(val.taxPercentage) : "",
      estimatedRate: val.price != null ? String(val.price) : "",
      description: val.specification || "",
      rateContract: false,
      vendorCode: "",
      vendorName: "",
      vendorMappings: [],
      budgetHead: budgetHeadName || prItem.budgetHead || "",
      categoryMappingId: matchingMapping?.mappingId || undefined,
      _item: val,
    };
    onUpdate({ ...baseUpdate, ...calculateItemAmounts(baseUpdate) });

    // Fetch approved, currently-valid vendor mappings → highest price → filter vendor dropdown
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.VENDOR_ITEM_MAPPING}`,
      null,
      {
        item: val._id || val.id,
        status: "approved",
        limit: 1000,
        ...(prDate ? { date: prDate } : {}),
      }
    )
      .then((res) => {
        const mappings = Array.isArray(res) ? res : res?.results || [];
        const approved = mappings.filter((m) => m.status === "approved");
        if (approved.length === 0) {
          setItemVendors(allVendors);
          return;
        }
        const vendorCodes = new Set(approved.map((m) => m.vendorCode).filter(Boolean));
        const filteredVendors = allVendors.filter((v) => vendorCodes.has(v.code));

        // A Vendor Item Mapping must never blindly override the Item Master price -- compare
        // the two and use whichever is higher, along with its own paired tax rate (never mix
        // a price from one source with a tax rate from the other). `rateContract` still flips
        // to true (an active mapping exists for this item) even on the rows where the Item
        // Master price wins.
        const bestVendorMapping = approved.reduce(
          (best, m) => ((parseFloat(m.price) || 0) > (parseFloat(best.price) || 0) ? m : best),
          approved[0]
        );
        const vendorPrice = parseFloat(bestVendorMapping.price) || 0;
        const vendorTax = bestVendorMapping.taxPercentage != null ? parseFloat(bestVendorMapping.taxPercentage) || 0 : 0;
        const itemMasterPrice = val.price != null ? parseFloat(val.price) || 0 : 0;
        const itemMasterTax = val.taxPercentage != null ? parseFloat(val.taxPercentage) || 0 : 0;
        const vendorWins = vendorPrice >= itemMasterPrice;
        const resolvedRate = vendorWins ? vendorPrice : itemMasterPrice;
        const resolvedTax = vendorWins ? vendorTax : itemMasterTax;

        const updated = {
          ...baseUpdate,
          estimatedRate: String(resolvedRate),
          taxRate: String(resolvedTax),
          rateContract: true,
          vendorMappings: approved,
        };
        setItemVendors(filteredVendors.length > 0 ? filteredVendors : allVendors);
        onUpdate({ ...updated, ...calculateItemAmounts(updated) });
      })
      .catch(() => setItemVendors(allVendors));
  };

  const handleVendorChange = (val) => {
    const updated = {
      ...prItem,
      vendorCode: val?.code || "",
      vendorName: val?.name || "",
    };
    onUpdate({ ...updated, ...calculateItemAmounts(updated) });
  };

  const isRateReadonly = prItem.rateContract;

  return (
    <div className="pr-item-row">
      {/* 0. Subcategory -- resolved from the catalog by id first (the authoritative value
          for a row loaded from an existing PR or an Excel upload), falling back to the
          row's own _subCategory only until subcategoryList has loaded. */}
      <Autocomplete
        value={
          subcategoryList.find((sc) => String(sc._id || sc.id) === String(prItem.subCategory)) ||
          prItem._subCategory ||
          null
        }
        onChange={(e, val) => handleSubcategoryChange(val)}
        options={subcategoryList}
        getOptionLabel={(op) => op.name || ""}
        isOptionEqualToValue={(opt, val) => (opt._id || opt.id) === (val._id || val.id)}
        renderInput={(params) => (
          <TextField {...params} size="small" label="Subcategory" />
        )}
      />

      {/* 1. Item — falls back to the pre-resolved _item (Excel upload/edit-loaded rows)
          until itemsForRow finishes loading for the row's subcategory */}
      <Autocomplete
        value={itemsForRow.find((i) => (i._id || i.id) === prItem.item) || prItem._item || null}
        onChange={(e, val) => handleItemChange(val)}
        options={itemsForRow}
        getOptionLabel={(op) => (op.itemCode ? `${op.itemCode} - ${op.name}` : op.name || "")}
        renderInput={(params) => (
          <TextField {...params} size="small" label="Item" />
        )}
      />

      {/* 2. Description */}
      <TextField
        size="small"
        label="Description"
        value={prItem.description || ""}
        onChange={(e) => handleFieldChange("description", e.target.value)}
        InputProps={{ readOnly: !!prItem.item }}
      />

      {/* 3. HSN */}
      <TextField
        size="small"
        label="HSN"
        value={prItem.hsnCode || ""}
        onChange={(e) => handleFieldChange("hsnCode", e.target.value)}
        InputProps={{ readOnly: !!prItem.item }}
      />

      {/* 4. UOM */}
      <TextField
        size="small"
        label="UOM"
        value={prItem.uom || ""}
        onChange={(e) => handleFieldChange("uom", e.target.value)}
        InputProps={{ readOnly: !!prItem.item }}
      />

      {/* 5. Qty -- per store once more than one target store is selected. The per-store
          override toggle lives directly under the field (not as a separate grid item) so
          it doesn't shift every column after it out of alignment with the header row. */}
      <div className="pr-item-row-qty-cell">
        <TextField
          size="small"
          label={isMultiStore ? "Qty (per store) *" : "Qty *"}
          type="number"
          value={prItem.quantity || ""}
          onChange={(e) =>
            usesAllocations ? handlePooledQuantityChange(e.target.value) : handleFlatQuantityChange(e.target.value)
          }
          inputProps={{ min: 0 }}
        />
        {isMultiStore && (
          <IconButton
            size="small"
            className="pr-item-row-qty-toggle"
            onClick={(e) => setPerStoreAnchor(e.currentTarget)}
            title="View/override per-store quantities"
          >
            <ExpandMore fontSize="small" />
          </IconButton>
        )}
      </div>

      {/* 6. Rate — readonly when rate contract */}
      <TextField
        size="small"
        label={prItem.rateContract ? "Rate (Contract)" : "Rate (₹) *"}
        type="number"
        value={prItem.estimatedRate || ""}
        onChange={(e) => handleFieldChange("estimatedRate", e.target.value)}
        inputProps={{ min: 0 }}
        InputProps={{ readOnly: isRateReadonly }}
      />

      {/* 7. Tax % */}
      <TextField
        size="small"
        label="Tax %"
        type="number"
        value={prItem.taxRate || ""}
        onChange={(e) => handleFieldChange("taxRate", e.target.value)}
        inputProps={{ min: 0, max: 100 }}
        InputProps={{ readOnly: !!prItem.item }}
      />

      {/* 8. Amount */}
      <TextField
        size="small"
        label="Amount"
        disabled
        value={prItem.estimatedAmount ? `₹${Number(prItem.estimatedAmount).toFixed(2)}` : ""}
      />

      {/* 9. Tax Amt */}
      <TextField
        size="small"
        label="Tax Amt"
        disabled
        value={prItem.estimatedTaxAmount ? `₹${Number(prItem.estimatedTaxAmount).toFixed(2)}` : ""}
      />

      {/* 10. Total */}
      <TextField
        size="small"
        label="Total"
        disabled
        value={prItem.estimatedTotalAmount ? `₹${Number(prItem.estimatedTotalAmount).toFixed(2)}` : ""}
      />

      {/* 11. Vendor (filtered to approved mappings) */}
      <Autocomplete
        value={itemVendors.find((v) => v.code === prItem.vendorCode) || null}
        onChange={(e, val) => handleVendorChange(val)}
        options={itemVendors}
        getOptionLabel={(op) => (op.code ? `${op.code} - ${op.name}` : op.name || "")}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={prItem.rateContract ? "Vendor (Rate Contract)" : "Vendor"}
          />
        )}
      />

      {/* 12. Required By Date */}
      <TextField
        size="small"
        label="Req. By Date"
        type="date"
        value={prItem.requiredByDate || headerRequiredByDate || ""}
        onChange={(e) => handleFieldChange("requiredByDate", e.target.value)}
        InputLabelProps={{ shrink: true }}
      />

      {/* 13. Budget Head (readonly) */}
      <TextField size="small" label="Budget Head" value={budgetHeadName || ""} disabled />

      {/* 14. Remarks -- view/edit via modal instead of an inline field */}
      <div className="pr-item-remarks-cell">
        <IconButton
          size="small"
          onClick={() => setRemarksOpen(true)}
          title={prItem.remarks ? "View/edit remarks" : "Add remarks"}
        >
          {prItem.remarks ? (
            <Comment fontSize="small" color="primary" />
          ) : (
            <CommentOutlined fontSize="small" />
          )}
        </IconButton>
      </div>

      {/* 15. Rate-contract chip + delete */}
      <div className="pr-row-actions">
        {prItem.rateContract && (
          <Chip label="RC" size="small" color="info" title="Rate Contract" />
        )}
        <IconButton color="error" size="small" onClick={onDelete}>
          <Delete fontSize="small" />
        </IconButton>
      </div>

      {/* 16. Per-store quantity override -- seeded equally by the flat Qty field above,
          adjustable per store here. Hidden for single-store items. */}
      {isMultiStore && (
        <Popover
          open={isPerStoreOpen}
          anchorEl={perStoreAnchor}
          onClose={() => setPerStoreAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          classes={{ paper: "finance-v2-pr-item-row-store-allocations-popover" }}
        >
          <Typography variant="caption" color="text.secondary" className="finance-v2-pr-item-row-store-allocations-title">
            Per-store quantity
          </Typography>
          <div className="finance-v2-pr-item-row-store-allocations-inner">
            {targetStores.map((store) => {
              const storeId = store._id || store.id;
              const allocation = (prItem.allocations || []).find((a) => String(a.storeId) === String(storeId));
              return (
                <TextField
                  key={storeId}
                  size="small"
                  label={store.code || store.name}
                  type="number"
                  value={allocation ? allocation.requestedQuantity : ""}
                  onChange={(e) => handleStoreAllocationChange(storeId, e.target.value)}
                  inputProps={{ min: 0 }}
                />
              );
            })}
          </div>
        </Popover>
      )}

      <RemarksDialog
        open={remarksOpen}
        initialValue={prItem.remarks}
        onClose={() => setRemarksOpen(false)}
        onSave={(value) => {
          handleFieldChange("remarks", value);
          setRemarksOpen(false);
        }}
      />
    </div>
  );
};

export default PRItemRow;
